<template>
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    :bg-color="currentView === 'timeline' ? '' : 'white'"
    :no-content-padding="currentView === 'timeline' || currentView === 'hooks'"
    :no-bottom-content-padding="currentView === 'hooks'"
    :main-layout-scroll-class="currentView === 'hooks' ? 'overflow-y-hidden' : 'overflow-y-auto'"
    :style="layoutStyleOverride"
  >
    <!-- Layout Toolbar -->
    <template #toolbar>
      <div>
        <div class="flex items-center px-5 pt-5 pb-2.5 gap-4 transition-opacity"
        :class="loadingBrandInfo ? 'opacity-0' : 'opacity-100'">
          <!-- BRAND ROUTE SWITCHER -->
          <div class="flex items-center">
            <router-link :to="{name: 'SpyderBrandsView'}" class="px-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M11.3217 13.3333L8.57757 10.5893C8.25214 10.2638 8.25214 9.73618 8.57757 9.41074L11.3217 6.66666"
                stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </router-link>
            <!-- Folder switcher (dropdown opener) -->
            <button v-if="!folderSwitcherExpanded" class="brand-route-switcher rounded-md flex items-center pl-2 pr-1 py-1.5"
            :class="{'enabled' : folderRoutes.length > 0}"
            @click="() => { if (folderRoutes.length > 0) { setFolderSwitcherExpanded(true); setBrandSwitcherExpanded(false) }}">
              <FolderIcon class="text-icon-normal mr-2" />
              <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                {{ folder.name }}
              </BaseText>
              <ChevronGrabberIcon v-if="folderRoutes.length > 0" class="text-icon-disabled" />
            </button>
            <!-- Folder switcher (dropdown) -->
            <div v-else ref="folder-switcher-spacer" class="relative h-8">
              <div ref="folder-switcher" v-on-clickaway="() => { setFolderSwitcherExpanded(false) }"
              class="brand-switcher-dropdown absolute w-max -top-1 -left-1 flex flex-col gap-1 px-1 pt-1 rounded-lg bg-neutral-25 z-50">
                <button class="flex items-center rounded-md pl-2 pr-1 py-1.5 box-shadow bg-white"
                @click="setFolderSwitcherExpanded(false)">
                  <FolderIcon class="text-icon-normal mr-2" />
                  <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                    {{ folder.name }}
                  </BaseText>
                  <ChevronGrabberIcon class="ml-auto text-icon-disabled" />
                </button>
                <div class="flex flex-col gap-1 overflow-y-scroll scrollbar-hide" style="max-height: 236px"
                :class="folderRoutes.length > 6 ? 'pb-2' : 'pb-1'">
                  <router-link v-for="folderRoute in folderRoutes" :key="`folder-${folderRoute.name}`"
                  :to="{name: 'SpyderView', params: currentView === 'ad-library' ? { id: folderRoute.brandId } : { id: folderRoute.brandId, view: currentView }}"
                  class="flex items-center rounded-md pl-2 pr-1 py-1.5 transition-colors hover:bg-neutral-50">
                    <FolderIcon class="text-icon-normal mr-2" />
                    <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                      {{ folderRoute.name }}
                    </BaseText>
                  </router-link>
                </div>
                <div v-if="folderRoutes.length > 6" class="absolute bottom-0 left-0 right-0 h-7 bg-gradient-to-t from-neutral-25 to-transparent rounded-b-lg opacity-90 pointer-events-none" />
              </div>
            </div>
            <!-- divider -->
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M11 4.00003L8.91622 15.8177" stroke="#C1C7D0" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <!-- Brand switcher (dropdown opener) -->
            <button v-if="!brandSwitcherExpanded" class="brand-route-switcher rounded-md flex items-center pl-2 pr-1 py-1.5"
            :class="{'enabled' : folderBrands.length > 0}"
            @click="() => { if (folderBrands.length > 0) { setBrandSwitcherExpanded(true); setFolderSwitcherExpanded(false) }}">
              <img :src="brand.avatar" class="w-5 h-5 mr-2" style="border-radius: 4.5px">
              <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                {{ brand.name }}
              </BaseText>
              <ChevronGrabberIcon v-if="folderBrands.length > 0" class="text-icon-disabled" />
            </button>
            <!-- Brand switcher (dropdown) -->
            <div v-else ref="brand-switcher-spacer" class="relative h-8">
              <div ref="brand-switcher" v-on-clickaway="() => { setBrandSwitcherExpanded(false) }"
              class="brand-switcher-dropdown absolute w-max -top-1 -left-1 flex flex-col gap-1 px-1 pt-1 rounded-lg bg-neutral-25 z-50">
                <button class="flex items-center rounded-md pl-2 pr-1 py-1.5 box-shadow bg-white"
                @click="setBrandSwitcherExpanded(false)">
                  <img :src="brand.avatar" class="w-5 h-5 mr-2" style="border-radius: 4.5px">
                  <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                    {{ brand.name }}
                  </BaseText>
                  <ChevronGrabberIcon class="ml-auto text-icon-disabled" />
                </button>
                <div class="flex flex-col gap-1 overflow-y-scroll scrollbar-hide" style="max-height: 236px"
                :class="folderBrands.length > 6 ? 'pb-2' : 'pb-1'">
                  <router-link v-for="folderBrand in folderBrands" :key="`switcher-${folderBrand.id}`"
                  :to="{name: 'SpyderView', params: currentView === 'ad-library' ? { id: folderBrand.id } : { id: folderBrand.id, view: currentView }}"
                  class="flex items-center rounded-md pl-2 pr-1 py-1.5 transition-colors hover:bg-neutral-50">
                    <img :src="folderBrand.avatar" class="w-5 h-5 mr-2" style="border-radius: 4.5px">
                    <BaseText type="label" size="sm" class="text-text-muted mr-1 whitespace-nowrap truncate" style="max-width: 200px">
                      {{ folderBrand.name }}
                    </BaseText>
                  </router-link>
                </div>
                <div v-if="folderBrands.length > 6" class="absolute bottom-0 left-0 right-0 h-7 bg-gradient-to-t from-neutral-25 to-transparent rounded-b-lg opacity-90 pointer-events-none" />
              </div>
            </div>
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-0.5" style="max-height: 40px;">
            <!-- Last updated label -->
            <div class="bg-neutral-25 px-2.5 py-1 rounded-full">
              <BaseText type="body" size="xs" class="text-text-muted">
                {{ brandLastUpdated }}
              </BaseText>
            </div>
            <!-- Data since label -->
            <div class="bg-neutral-25 px-2.5 py-1 rounded-full">
              <BaseText type="body" size="xs" class="text-text-muted">
                Data since {{ formatDate(subscription.createdAt) }}
              </BaseText>
            </div>
          </div>
          <div class="ml-auto flex items-center gap-3">
            <button id="spyder-view-tutorial-button" class="flex items-center gap-1.5 pl-2 py-1.5 pr-3 rounded-md transition-colors hover:bg-neutral-25"
            @click="tutorialTimestamp = 74, showTutorialModal = true">
              <PlayIcon class="text-icon-normal" />
              <div class="text-sm leading-5 font-medium text-text-muted">
                Tutorial
              </div>
            </button>
            <!-- <button class="flex items-center gap-1.5 pl-2 py-1.5 pr-3 rounded-md transition-colors hover:bg-neutral-25">
              <AdChangelogIcon class="text-icon-normal" />
              <div class="text-sm leading-5 font-medium text-text-muted">
                Ad Changelog
              </div>
            </button> -->
            <div @mouseenter="shareButtonHovered = true" @mouseleave="shareButtonHovered = false">
              <BaseButton primary icon-only class="relative cursor-default">
                <div class="relative flex items-center gap-1.5 pl-1 pr-1.5">
                  <LinkIcon class="text-white" />
                  <BaseText type="label" size="sm" class="text-white flex">
                    Share
                  </BaseText>
                </div>
                <transition>
                  <div v-if="shareButtonHovered" class="absolute top-full right-0 pt-2.5"
                  @mouseenter="shareButtonHovered = false">
                    <div class="relative bg-neutral-800 rounded-lg py-1.5 px-4 shadow-md">
                      <BaseText type="label" size="sm" class="text-white">
                        Shared links coming soon!
                      </BaseText>
                      <div class="arrow-top absolute bottom-full" style="right: 37px" />
                    </div>
                  </div>
                </transition>
              </BaseButton>
            </div>
          </div>
        </div>
        <!-- SUBVIEW TABS -->
        <div class="flex gap-2 px-5 py-2">
          <div class="brand-view-tab" :class="{'active': currentView === 'ad-library'}"
          @click="changeView('ad-library')">
            <AdLibraryIcon class="w-5 h-5" />
            Ad Library
          </div>
          <div class="brand-view-tab" :class="{'active': currentView === 'creative-tests'}"
          @click="changeView('creative-tests')">
            <CreativeTestsIcon class="w-5 h-5" />
            Creative Tests
          </div>
          <div class="brand-view-tab" :class="{'active': currentView === 'landing-pages'}"
          @click="changeView('landing-pages')">
            <LandingPagesIcon class="w-5 h-5" />
            Landing Pages
          </div>
          <div class="brand-view-tab" :class="{'active': currentView === 'hooks'}"
          @click="changeView('hooks')">
            <HooksIcon class="w-5 h-5" />
            Hooks
          </div>
          <div class="brand-view-tab" :class="{'active': currentView === 'timeline'}"
          @click="changeView('timeline')">
            <TimelineIcon class="w-5 h-5" />
            Timeline
          </div>
        </div>
      </div>
    </template>
    <template #filter>
      <!-- We don't render the filter bar for creative tests, landing pages, or hooks (hooks owns its own filter bar) -->
      <div v-if="['creative-tests', 'landing-pages', 'hooks'].includes(currentView)" class="w-full border-t border-border-normal" />
      <div v-else ref="filterBar" class="border hidden sm:block bg-white bg-opacity-90 border-border-normal"
      :class="currentView === 'timeline' && advertisements.length ? 'border-b-0 border-r-0' : 'border-border-normal'"
      style="backdrop-filter: blur(6px)">
        <FilterTopBar
          ref="filterTopBar"
          discovery
          :active-filters.sync="activeFilters"
          :selected-date-range.sync="selectedDateRange"
          :sort-order.sync="sortOrder"
          :text-search.sync="textSearch"
          :excluded-filter-types="excludedFilterTypes"
          :unique-creatives.sync="localUniqueCreatives"
          @change="fetchAdvertisements"
        />
        <!-- Expand the filter bar with added info when using timeline view -->
        <div v-if="currentView === 'timeline' && advertisements.length" class="flex border-t border-gray-300">
          <div class="border-r border-border-normal" style="width: 231px">
            <div class="flex gap-1 h-1/2 w-full border-border-normal items-center pl-3 border-b" style="margin-top: 0.5px">
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                <path d="M4.515 17.1933L4.85549 16.5251H4.85549L4.515 17.1933ZM3.78664 16.465L4.45489 16.1245H4.45489L3.78664 16.465ZM17.1733 16.465L16.5051 16.1245L16.5051 16.1245L17.1733 16.465ZM16.445 17.1933L16.1045 16.5251L16.1045 16.5251L16.445 17.1933ZM17.1733 4.53502L16.5051 4.87551L16.5051 4.87551L17.1733 4.53502ZM16.445 3.80666L16.1045 4.47491L16.1045 4.47491L16.445 3.80666ZM3.78664 4.53502L3.11838 4.19452H3.11838L3.78664 4.53502ZM4.515 3.80666L4.1745 3.1384H4.1745L4.515 3.80666ZM16.605 6.29167V14.7083H18.105V6.29167H16.605ZM14.6883 16.625H6.27165V18.125H14.6883V16.625ZM4.35498 14.7083V6.29167H2.85498V14.7083H4.35498ZM6.27165 4.375H14.6883V2.875H6.27165V4.375ZM6.27165 16.625C5.79256 16.625 5.47835 16.6244 5.23806 16.6048C5.00652 16.5859 4.9104 16.5531 4.85549 16.5251L4.1745 17.8616C4.47611 18.0153 4.79161 18.0733 5.11591 18.0998C5.43146 18.1256 5.81731 18.125 6.27165 18.125V16.625ZM2.85498 14.7083C2.85498 15.1627 2.8544 15.5485 2.88018 15.8641C2.90667 16.1884 2.9647 16.5039 3.11838 16.8055L4.45489 16.1245C4.42691 16.0696 4.39411 15.9735 4.3752 15.7419C4.35556 15.5016 4.35498 15.1874 4.35498 14.7083H2.85498ZM4.85549 16.5251C4.68301 16.4372 4.54278 16.297 4.45489 16.1245L3.11838 16.8055C3.35007 17.2602 3.71978 17.6299 4.1745 17.8616L4.85549 16.5251ZM16.605 14.7083C16.605 15.1874 16.6044 15.5016 16.5848 15.7419C16.5658 15.9735 16.533 16.0696 16.5051 16.1245L17.8416 16.8055C17.9953 16.5039 18.0533 16.1884 18.0798 15.8641C18.1056 15.5485 18.105 15.1627 18.105 14.7083H16.605ZM14.6883 18.125C15.1426 18.125 15.5285 18.1256 15.8441 18.0998C16.1684 18.0733 16.4838 18.0153 16.7855 17.8616L16.1045 16.5251C16.0496 16.5531 15.9534 16.5859 15.7219 16.6048C15.4816 16.6244 15.1674 16.625 14.6883 16.625V18.125ZM16.5051 16.1245C16.4172 16.297 16.277 16.4372 16.1045 16.5251L16.7855 17.8616C17.2402 17.6299 17.6099 17.2602 17.8416 16.8055L16.5051 16.1245ZM18.105 6.29167C18.105 5.83733 18.1056 5.45147 18.0798 5.13593C18.0533 4.81163 17.9953 4.49613 17.8416 4.19452L16.5051 4.87551C16.533 4.93042 16.5658 5.02654 16.5848 5.25808C16.6044 5.49837 16.605 5.81258 16.605 6.29167H18.105ZM14.6883 4.375C15.1674 4.375 15.4816 4.37558 15.7219 4.39522C15.9534 4.41413 16.0496 4.44693 16.1045 4.47491L16.7855 3.1384C16.4838 2.98472 16.1684 2.92669 15.8441 2.9002C15.5285 2.87442 15.1426 2.875 14.6883 2.875V4.375ZM17.8416 4.19452C17.6099 3.7398 17.2402 3.37009 16.7855 3.1384L16.1045 4.47491C16.277 4.5628 16.4172 4.70303 16.5051 4.87551L17.8416 4.19452ZM4.35498 6.29167C4.35498 5.81258 4.35556 5.49837 4.3752 5.25808C4.39411 5.02654 4.42691 4.93042 4.45489 4.87551L3.11838 4.19452C2.9647 4.49613 2.90667 4.81163 2.88018 5.13593C2.8544 5.45147 2.85498 5.83733 2.85498 6.29167H4.35498ZM6.27165 2.875C5.81731 2.875 5.43146 2.87442 5.11591 2.9002C4.79161 2.92669 4.47611 2.98472 4.1745 3.1384L4.85549 4.47491C4.9104 4.44693 5.00652 4.41413 5.23806 4.39522C5.47835 4.37558 5.79256 4.375 6.27165 4.375V2.875ZM4.45489 4.87551C4.54278 4.70303 4.68301 4.56279 4.85549 4.47491L4.1745 3.1384C3.71978 3.37009 3.35008 3.7398 3.11838 4.19452L4.45489 4.87551Z" fill="#5E6678" />
                <path d="M11.0366 13.2917C10.6224 13.2917 10.2866 13.6275 10.2866 14.0417C10.2866 14.456 10.6224 14.7917 11.0366 14.7917L11.0366 13.2917ZM14.0366 14.7917C14.4508 14.7917 14.7866 14.456 14.7866 14.0417C14.7866 13.6275 14.4508 13.2917 14.0366 13.2917L14.0366 14.7917ZM11.0366 14.7917L14.0366 14.7917L14.0366 13.2917L11.0366 13.2917L11.0366 14.7917Z" fill="#5E6678" />
                <path d="M7.97998 9.75708C7.56577 9.75708 7.22998 10.0929 7.22998 10.5071C7.22998 10.9213 7.56577 11.2571 7.97998 11.2571V9.75708ZM12.98 11.2571C13.3942 11.2571 13.73 10.9213 13.73 10.5071C13.73 10.0929 13.3942 9.75708 12.98 9.75708V11.2571ZM7.97998 11.2571H12.98V9.75708H7.97998V11.2571Z" fill="#5E6678" />
                <path d="M6.93848 6.22266C6.52426 6.22266 6.18848 6.55844 6.18848 6.97266C6.18848 7.38687 6.52426 7.72266 6.93848 7.72266V6.22266ZM9.93848 7.72266C10.3527 7.72266 10.6885 7.38687 10.6885 6.97266C10.6885 6.55844 10.3527 6.22266 9.93848 6.22266V7.72266ZM6.93848 7.72266H9.93848V6.22266H6.93848V7.72266Z" fill="#5E6678" />
              </svg>
              <BaseText type="label" size="sm" class="text-text-muted">
                Timeline
              </BaseText>
            </div>
            <div class="flex gap-2 h-1/2 w-full border-border-normal items-center px-2 justify-end">
              <button class="flex items-center justify-center w-1/2 h-6 rounded border border-border-hover"
              @click="zoomOut">
                -
              </button>
              <button class="flex items-center justify-center w-1/2 h-6 rounded border border-border-hover"
              @click="zoomIn">
                +
              </button>
            </div>
          </div>
          <div class="flex py-2.5 flex-col gantt-timeaxis-animation overflow-y-visible overflow-x-hidden"
          style="width: calc(100% - 233px)">
            <div class="border-border-normal flex items-center justify-end w-full h-px border-b pt-7 mb-2" />
            <div class="flex items-center w-full justify-around pl-4">
              <div v-for="(item, index) in dateRange" :key="index" class="relative flex justify-end"
              :class="currentZoom === 'year' && 'h-5'" :style="{ width: item.widthPercentage + '%' }">
                <!-- Display day -->
                <BaseText v-if="currentZoom !== 'year'" type="body" size="sm" class="text-neutral-500 rounded select-none">
                  {{ item.day }}
                </BaseText>
                <!-- Display month (if available) -->
                <div v-if="item.month" class="absolute -top-10" :class="{ 'left-5': currentZoom === 'month', 'left-20 pl-5': currentZoom === 'week', 'left-1': currentZoom === 'year' }">
                  <BaseText type="label" size="sm" class="text-text-muted px-1 rounded cursor-default -top-12 select-none">
                    {{ item.month }}
                  </BaseText>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Layout Content -->
    <template #content>
      <AdLibraryBrandView
        v-if="currentView === 'ad-library'"
        :advertisements="advertisements"
        :loading-advertisements="loadingAdvertisements"
        :all-ads-loaded="allAdsLoaded"
        :brand="brand"
        :total="total"
        :filters-applied="filtersApplied"
        :has-search-query="textSearch.length > 0"
        @updateAds="updateAdsLocal"
        @getMoreAds="() => getMoreAds({})"
        @tabSwitch="changeView($event)"
        @clearFilters="clearFilters"
      />
      <CreativeTestsBrandView
        v-else-if="currentView === 'creative-tests'"
        :advertisements="advertisements"
        :loading-advertisements="loadingAdvertisements"
        @getMoreAds="() => getMoreAds({})"
        @openTutorialModal="tutorialTimestamp = 99, showTutorialModal = true"
      />
      <LandingPagesBrandView v-else-if="currentView === 'landing-pages'" />
      <HooksBrandView
        v-else-if="currentView === 'hooks'"
        :filters="{
          activeFilters,
          selectedDateRange,
          sortOrder,
          textSearch
        }"
        @update:dateRange="selectedDateRange = $event"
      />
      <TimelineBrandView
        v-else-if="currentView === 'timeline'"
        :advertisements="advertisements"
        :loading-advertisements="loadingAdvertisements"
        :my-chart-start.sync="myChartStart"
        :my-chart-end.sync="myChartEnd"
        :current-zoom="currentZoom"
        :zoom-in="zoomIn"
        :filters-applied="filtersApplied"
        :has-search-query="textSearch.length > 0"
        @getMoreAds="() => getMoreAds({})"
        @clearFilters="clearFilters"
      />
      <WinningAdsBrandView v-else-if="currentView === 'winning-ads'" />
      <BaseTutorialModal
        v-if="showTutorialModal"
        video-url="https://www.youtube.com/embed/zNj71_-IIVk?si=C5PRycUlLRkX86l8"
        :timestamp="tutorialTimestamp"
        @close="showTutorialModal = false"
      />
      <FixSpyderModal
        v-if="showSpyderFixModal"
        :spyder-limit="spyderLimit"
        @close="showSpyderFixModal = false"
      />
      <BaseLoadingLogo v-else-if="loadingAdvertisements && !advertisements.length" />
      <infinite-loading
        v-if="useInfiniteLoader && advertisements.length"
        :identifier="infiniteId"
        @infinite="getMoreAds"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </template>
  </MainLayout>
</template>
<script>
import firebase from 'firebase/app'
import ForeplayAPI from '@/api/foreplayServer'
import FirebaseAPI from '@/api/firebase'
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import { checkAndCacheAll, clearUniqueCreatives } from '@/db/UniqueCreativesDB'
import InfiniteLoading from 'vue-infinite-loading'
import moment from 'moment'

// Components
import MainLayout from '@/layouts/MainLayout.vue'
import FilterTopBar from '../../components/search/FilterTopBar.vue'

// Icons
import FolderIcon from '../../components/globals/Icons/SpyderV2Icons/FolderIcon.vue'
import ChevronGrabberIcon from '../../components/globals/Icons/ChevronGrabberIcon.vue'
import PlayIcon from '../../components/globals/Icons/PlayIcon.vue'
import AdChangelogIcon from '../../components/globals/Icons/SpyderV2Icons/AdChangelogIcon.vue'
import LinkIcon from '../../components/globals/Icons/LinkIcon.vue'

// Tab Icons
import AdLibraryIcon from '../../components/globals/Icons/SpyderBrandIcons/AdLibraryIcon.vue'
import CreativeTestsIcon from '../../components/globals/Icons/SpyderBrandIcons/CreativeTestsIcon.vue'
import LandingPagesIcon from '../../components/globals/Icons/SpyderBrandIcons/LandingPagesIcon.vue'
import HooksIcon from '../../components/globals/Icons/SpyderBrandIcons/HooksIcon.vue'
import TimelineIcon from '../../components/globals/Icons/SpyderBrandIcons/TimelineIcon.vue'

// Tab views
import AdLibraryBrandView from '../../components/spyderV2/brandViews/AdLibraryBrandView.vue'
import CreativeTestsBrandView from '../../components/spyderV2/brandViews/CreativeTestsBrandView.vue'
import HooksBrandView from '../../components/spyderV2/brandViews/HooksBrandView.vue'
import LandingPagesBrandView from '../../components/spyderV2/brandViews/LandingPagesBrandView.vue'
import TimelineBrandView from '../../components/spyderV2/brandViews/TimelineBrandView.vue'
import WinningAdsBrandView from '../../components/spyderV2/brandViews/WinningAdsBrandView.vue'
import FixSpyderModal from '../../components/spyderV2/FixSpyderModal.vue'

export default {
  name: 'SpyderView',
  components: {
    MainLayout,
    FilterTopBar,
    InfiniteLoading,
    AdLibraryBrandView,
    CreativeTestsBrandView,
    HooksBrandView,
    LandingPagesBrandView,
    TimelineBrandView,
    WinningAdsBrandView,
    FixSpyderModal,
    FolderIcon,
    ChevronGrabberIcon,
    PlayIcon,
    AdChangelogIcon,
    LinkIcon,
    AdLibraryIcon,
    CreativeTestsIcon,
    LandingPagesIcon,
    HooksIcon,
    TimelineIcon
  },
  mixins: [clickaway],
  data () {
    return {
      // Tab Control
      currentView: 'ad-library',

      // Data States
      brand: {},
      folder: {},
      advertisements: [],
      localUniqueCreatives: true,

      // Spyder Subscription States
      subscription: {},
      showSpyderFixModal: false,
      spyderLimit: 2,

      // Pagination States
      lastDocId: null,
      infiniteId: +new Date(),
      total: 0,
      
      // Loading States
      loadingAdvertisements: true,
      allAdsLoaded: false,
      loadingBrandInfo: true,
      
      // Ad Library View Filter Bar States
      textSearch: '',
      activeFilters: {},
      selectedDateRange: {
        start: null,
        end: null
      },
      sortOrder: [{ name: 'Newest', value: 'desc' }],
      adView: 'grid',

      // Timeline View Toolbar States
      myChartStart: null,
      myChartEnd: null,
      currentZoom: 'month',
      timerangeStart: '',
      timerangeEnd: '',
      viewOptions: [
        { name: 'Gantt', value: 'gantt' },
        { name: 'Grid', value: 'grid' }
      ],

      // Page Header States
      brandSwitcherExpanded: false,
      folderSwitcherExpanded: false,
      brandLastUpdated: '',
      shareButtonHovered: false,

      // Tutorial Modal States
      showTutorialModal: false,
      tutorialTimestamp: 74,
    }
  },
  watch: {
    '$route.params.id' () {
      this.checkSpyderOverage()
    },
    currentView () {
      // When the view changes, we want to refetch advertisements
      // This prevents potentially loading too many ads at once into the DOM
      this.fetchAdvertisements(true, true)
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam', 'getUser', 'getSpyderLimit']),
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions']),
    ...mapGetters('SearchModule', ['getFilterCategories']),
    useInfiniteLoader () {
      // Right now, infinite loader is only enabled for ad library and timeline views
      return this.currentView === 'ad-library' || this.currentView === 'timeline'
    },
    folderBrands () { // For toolbar navigation
      // Returns the other brands in the same folder as the current brand
      let brands
      if (!this.folder.id) { // No folder id means current brand is in default folder
        brands = this.getSpyderSubscriptions.filter(sub =>
          !this.getFolders.some(folder => folder?.spyderBrands?.includes(sub.subscription.pageId))
        )
      } else {
        brands = this.folder.spyderBrands
          ? this.folder.spyderBrands.map(brandId => this.getSpyderSubscriptions.find(sub => sub.subscription.pageId === brandId)).filter(sub => sub)
          : []
      }
      return brands.filter(sub => sub.subscription.pageId !== this.brand.adLibraryId)
    },
    folderRoutes () { // For toolbar navigation
      // Returns names and the first brand id of the other folders
      const routes = []
      this.getFolders.forEach(folder => {
        if (!folder.spyderBrands || folder.spyderBrands.length < 1 || folder.id === this.folder.id) { return } // Skip the currently active folder and folders with no brands
        const firstBrand = this.getSpyderSubscriptions.find(sub => sub.subscription.pageId === folder.spyderBrands[0])
        if (firstBrand) { // Only add folders with brands
          routes.push({
            name: folder.name,
            brandId: firstBrand.id
          })
        }
      })
      // Add the default folder (if we're not in it already)
      if (this.folder.id) {
        // Get the first brand that isn't in any folder
        const defaultBrand = this.getSpyderSubscriptions.find(sub =>
          !this.getFolders.some(folder => folder?.spyderBrands?.includes(sub.subscription.pageId))
        )
        if (defaultBrand) {
          routes.push({
            name: 'Default Folder',
            brandId: defaultBrand.id
          })
        }
      }
      return routes
    },
    dateRange () {
      const startDate = moment(this.myChartStart).startOf('day')
      const endDate = moment(this.myChartEnd).endOf('day')
      const result = []
      let currentMonth = ''

      const totalDays = endDate.diff(startDate, 'days') + 1
      const widthPercentage = 100 / totalDays

      while (startDate.isSameOrBefore(endDate, 'day')) {
        if (this.currentZoom !== 'year') {
          const currentItem = {
            day: startDate.format('D'),
            widthPercentage,
            barLeftPosition: this.mapTimeToPosition(startDate),
            barWidth: this.mapTimeToPosition(startDate.clone().add(1, 'days'))
          }

          if (
            (startDate.date() === 1 ||
          startDate.isSame(startDate.clone().startOf('month'), 'day')) &&
        startDate.format('MMMM') !== currentMonth
          ) {
            currentItem.month = startDate.format(
              this.currentZoom === 'year' ? 'MMM' : 'MMMM'
            )
            currentMonth = startDate.format('MMMM')
          }

          result.push(currentItem)
        } else {
          if (
            startDate.date() === 1 &&
        startDate.format('MMMM') !== currentMonth
          ) {
            const monthDays = startDate.daysInMonth()
            const monthWidthPercentage = widthPercentage * monthDays

            const monthItem = {
              month: startDate.format('MMM'),
              widthPercentage: monthWidthPercentage,
              barLeftPosition: this.mapTimeToPosition(startDate),
              barWidth: this.mapTimeToPosition(startDate.clone().add(monthDays, 'days'))
            }

            currentMonth = startDate.format('MMMM')
            result.push(monthItem)
          }
        }
        startDate.add(1, 'day')
      }

      return result
    },
    excludedFilterTypes () {
      return this.currentView === 'hooks' ? ['format'] : []
    },
    filtersApplied () {
      return Object.keys(this.activeFilters).length > 0 || this.textSearch.length > 0
    },
    isSubscribedToBrand () {
      if (!this.brand?.adLibraryId) return true
      return this.getSpyderSubscriptions.some(
        sub => sub.subscription?.pageId === this.brand.adLibraryId
      )
    },
    layoutStyleOverride () {
      if (this.currentView === 'landing-pages') return 'height: calc(100vh - 30px); overflow: hidden;'
      // if (this.currentView === 'hooks') return 'height: calc(100vh - 100px);'
      return ''
    }
  },
  beforeMount () {
    const routedView = this.$route.params.view
    if (routedView) this.currentView = routedView
  },
  async mounted () {
    this.spyderLimit = this.getSpyderLimit

    await Promise.all([
      this.fetchSpyderSubscriptions(),
      this.checkSpyderOverage()
    ])

    // filter
    if (this.$refs?.filterBar) {
      this.$refs.filterBar.style.position = 'sticky'
      this.$refs.filterBar.style.top = '16px'
      this.$refs.filterBar.style.zIndex = '40000'
    }

    if (this.currentView === 'timeline') {
      this.$refs.filterBar.style.marginLeft = '0px'
      this.$refs.filterBar.style.marginRight = '0px'
      this.$refs.filterBar.style.top = '0px'
    }

    // brands
    await this.getUniqueBrand()

    this.guardTrackingBrand() // dont await block, let it resolve in background

    await this.getFbAdsPageTrack()
    this.computeBrandLastUpdated()

    // ads
    await this.fetchAdvertisements()

    this.myChartStart = moment().startOf('day').subtract(30, 'days').format('YYYY-MM-DD HH:mm')
    this.myChartEnd = moment().startOf('day').format('YYYY-MM-DD HH:mm')

    this.loadingBrandInfo = false
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    async checkSpyderOverage () {
      try {
        const { data: hasOverage } = await ForeplayAPI.Spyder.checkSpyderOverage()
        this.showSpyderFixModal = hasOverage || false
      } catch (error) {
        console.error(error)
      }
    },
    async guardTrackingBrand () {
      // Fast check - if fail, fallback to a hard query. Spyder cache can sometimes be stale
      if (this.isSubscribedToBrand) return

      try {
        const subscriptionId = this.getTeam?.id || this.getUser.user_id
        const isTrackingBrand = await FirebaseAPI.Spyder.checkIsTrackingBrand(subscriptionId, this.$route.params.id)
        if (!isTrackingBrand) {
          this.$showAlert({
            type: 'error',
            message: 'You are not tracking this brand. Add it to your Spyder to view.'
          })
          this.$router.push({ name: 'SpyderBrandsView' })
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.'
        })
      }
    },
    changeView (view) {
      if (this.showSpyderFixModal) return
      this.currentView = view
      const path = `/library-spyder/${this.$route.params.id}${view !== 'ad-library' ? `/${view}` : ''}`
      history.replaceState({}, null, path) // Push view param to route without triggering a reload
    },
    zoomIn () {
      const today = moment().startOf('day')
      const isEndToday = moment(this.myChartEnd).isSame(today, 'day')
      const midPoint = moment(this.myChartStart).add(moment(this.myChartEnd).diff(moment(this.myChartStart)) / 2)

      if (isEndToday) {
        if (this.currentZoom === 'month') {
          this.myChartStart = today.clone().subtract(7, 'days').format('YYYY-MM-DD HH:mm')
          this.myChartEnd = today.format('YYYY-MM-DD HH:mm')
          this.currentZoom = 'week'
        } else if (this.currentZoom === 'year') {
          this.myChartStart = today.clone().subtract(1, 'month').format('YYYY-MM-DD HH:mm')
          this.myChartEnd = today.format('YYYY-MM-DD HH:mm')
          this.currentZoom = 'month'
        }
      } else {
        if (this.currentZoom === 'month') {
          this.myChartStart = midPoint.clone().subtract(7.5, 'days').format('YYYY-MM-DD HH:mm')
          this.myChartEnd = midPoint.clone().add(7.5, 'days').format('YYYY-MM-DD HH:mm')
          this.currentZoom = 'week'
        } else if (this.currentZoom === 'year') {
          this.myChartStart = midPoint.clone().subtract(15, 'days').format('YYYY-MM-DD HH:mm')
          this.myChartEnd = midPoint.clone().add(15, 'days').format('YYYY-MM-DD HH:mm')
          this.currentZoom = 'month'
        }
      }
    },
    zoomOut () {
      const today = moment().startOf('day')
      const midPoint = moment(this.myChartStart).add(moment(this.myChartEnd).diff(moment(this.myChartStart)) / 2)

      const calculateNewEnd = (increment) => midPoint.clone().add(increment, 'days')

      let endAfterToday = false // Initialize variable at the beginning

      if (this.currentZoom === 'week') {
        const newEnd = calculateNewEnd(15)
        endAfterToday = newEnd.isSameOrAfter(today)
      } else if (this.currentZoom === 'month') {
        const newEnd = calculateNewEnd(6 * 30) // Assuming an average of 30 days per month
        endAfterToday = newEnd.isSameOrAfter(today)
      }

      if (endAfterToday) {
        if (this.currentZoom === 'week') {
          this.myChartStart = today.clone().subtract(1, 'month').format('YYYY-MM-DD HH:mm')
          this.myChartEnd = today.format('YYYY-MM-DD HH:mm')
          this.currentZoom = 'month'
        } else if (this.currentZoom === 'month') {
          this.myChartStart = today.clone().subtract(1, 'year').format('YYYY-MM-DD HH:mm')
          this.myChartEnd = today.format('YYYY-MM-DD HH:mm')
          this.currentZoom = 'year'
        }
        return
      }

      // Existing zoom out logic
      if (this.currentZoom === 'week') {
        this.myChartStart = midPoint.clone().subtract(15, 'days').format('YYYY-MM-DD HH:mm')
        this.myChartEnd = midPoint.clone().add(15, 'days').format('YYYY-MM-DD HH:mm')
        this.currentZoom = 'month'
      } else if (this.currentZoom === 'month') {
        this.myChartStart = midPoint.clone().subtract(6, 'months').format('YYYY-MM-DD HH:mm')
        this.myChartEnd = midPoint.clone().add(6, 'months').format('YYYY-MM-DD HH:mm')
        this.currentZoom = 'year'
      }
    },
    mapTimeToPosition (time) {
      const start = moment(this.myChartStart)
      const end = moment(this.myChartEnd)

      const totalHourCount = end.diff(start, 'hours', true)

      const hourDiffFromStart = moment(time).diff(start, 'hours', true)
      const positionPercentage = (hourDiffFromStart / totalHourCount) * 100

      return positionPercentage
    },
    formatDate (dateMS) {
      const date = new Date(dateMS)
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return date.toLocaleDateString('en-US', options)
    },
    async getUniqueBrand () {
      const brandInstance = await FirebaseAPI.Brands.get(this.$route.params.id)
      this.brand = brandInstance

      // Get the brand's folder
      this.folder = this.getFolders.find((folder) => folder?.spyderBrands?.includes(brandInstance.adLibraryId)) ||
        { id: null, name: 'Default Folder' }
    },
    async getFbAdsPageTrack () {
      const db = firebase.firestore()
      const snapshot = await db
        .collection('fb_ads_page_track')
        .where('brandId', '==', this.$route.params.id)
        .get()

      const subscription = snapshot.docs.map((doc) => doc.data())[0]
      this.subscription = subscription
    },
    onScroll (e, position) {
      if (!this.$refs.filterBar) return

      const marginValue = position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding = position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      if (this.currentView === 'timeline') {
        this.$refs.filterBar.style.marginLeft = '0px'
        this.$refs.filterBar.style.marginRight = '0px'
        this.$refs.filterBar.style.top = '0px'
      } else {
        this.$refs.filterBar.style.marginLeft = `${marginValue}px`
        this.$refs.filterBar.style.marginRight = `${marginValue}px`
        this.$refs.filterBar.style.marginTop = `${marginValue}px`
      }
      this.$refs.filterBar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue ? position.scrollTop / 15 : maxValue
      }

      if (position.scrollTop) {
        this.$refs.filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          15
        )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          7
        )}px ${shadowSection(
          10
        )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          7
        )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          3
        )}px ${shadowSection(5)}px`
      } else {
        this.$refs.filterBar.style.boxShadow = null
      }

      if (marginValue === 16 && this.currentView !== 'timeline') {
        this.$refs.filterBar.style.position = 'sticky'
        this.$refs.filterBar.style.top = '16px'
        this.$refs.filterBar.style.zIndex = '40000'
      }
    },
    setBrandSwitcherExpanded (expanded) {
      console.log('setBrandSwitcherExpanded', expanded)
      this.brandSwitcherExpanded = expanded
      if (expanded) {
        this.$nextTick(() => {
          console.log(this.$refs)
          const spacer = this.$refs['brand-switcher-spacer']
          const switcher = this.$refs['brand-switcher']
          console.log(spacer, switcher)
          if (spacer && switcher) {
            spacer.style.width = `${switcher.offsetWidth - 8}px`
          }
        })
      }
    },
    setFolderSwitcherExpanded (expanded) {
      console.log('setFolderSwitcherExpanded', expanded)
      this.folderSwitcherExpanded = expanded
      if (expanded) {
        this.$nextTick(() => {
          const spacer = this.$refs['folder-switcher-spacer']
          const switcher = this.$refs['folder-switcher']
          if (spacer && switcher) {
            spacer.style.width = `${switcher.offsetWidth - 9}px`
          }
        })
      }
    },
    async fetchAdvertisements (load = true, refresh = false) {
      this.loadingAdvertisements = load
      this.advertisements = []

      await clearUniqueCreatives()

      const ranBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange?.end) || null
      if (refresh) {
        this.lastDocId = null
      }
      try {
        const { results, nextPage } = await ForeplayAPI.Ads.getSpyderAds(null, {
          ...this.activeFilters,
          ranBetweenStart,
          ranBetweenEnd,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value,
          orBrands: [this.$route.params.id]
        })

        this.lastDocId = nextPage

        if (this.localUniqueCreatives) {
          const uniqueCreatives = await checkAndCacheAll(results)
          console.log('uniqueCreatives', uniqueCreatives)
          this.advertisements = uniqueCreatives
        } else {
          this.advertisements = results
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
      }
    },
    async getMoreAds ($state) {
      console.log('GETTING MORE ADS')
      if (this.showSpyderFixModal) {
        return
      }

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.allAdsLoaded = true
        return
      }

      const ranBetweenStart = +new Date(this.selectedDateRange.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange.end) || null
      const { results, nextPage } = await ForeplayAPI.Ads.getSpyderAds(this.lastDocId, {
        ...this.activeFilters,
        ranBetweenStart,
        ranBetweenEnd,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value,
        orBrands: [this.$route.params.id]
      })
      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      if (this.localUniqueCreatives) {
        const uniqueCreatives = await checkAndCacheAll(results)
        this.advertisements = [...this.advertisements, ...uniqueCreatives]
      } else {
        this.advertisements = [...this.advertisements, ...results]
      }

      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    updateAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.advertisements]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.advertisements = duplicateAds
    },
    computeBrandLastUpdated () {
      const time = this.subscription.last_checked.seconds
      const updatedAgo = ((new Date().getTime() - (time * 1000)) / 3600000)
      const timeStr = updatedAgo < 1 ? `${Math.floor(updatedAgo * 60)}m` : `${Math.floor(updatedAgo)}h`
      this.brandLastUpdated = `Updated ${timeStr} ago`
    },
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''

      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    }
  }
}
</script>

<style scoped>
.brand-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
}
.brand-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.brand-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
.brand-route-switcher {
  transition: box-shadow 150ms ease-in-out;
  box-shadow: none;
  cursor: default;
}
.brand-route-switcher.enabled {
  cursor: pointer;
}
.brand-route-switcher.enabled:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.brand-switcher-dropdown {
  z-index: 40002;
}
.box-shadow {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.timeaxis-line--day {
  margin-top: 30px;
  margin-left: 3px;
}
.timeaxis-line--month {
  margin-top: 70px;
}
.hooks-bg {
  background-color: #F6F8FA;
}
.arrow-top {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 7px solid #06070F;
}
</style>
